import React, { useState, useEffect } from 'react';

interface ConnectionRequest {
  id: number;
  from: string;
  to: string;
  status: 'pending' | 'accepted';
}

interface BuzzNotification {
  from: string;
  to: string;
  message: string;
  timestamp: string;
}

const ChatConnectionSystem = () => {
  const [connectionRequests, setConnectionRequests] = useState<ConnectionRequest[]>([
    { id: 1, from: 'Business Security 28', to: 'SuperKonnected 28', status: 'pending' }
  ]);
  
  const [notifications, setNotifications] = useState<any[]>([]);
  const [onlineUsers, setOnlineUsers] = useState<string[]>(['SuperKonnected 28']);
  const [showPopup, setShowPopup] = useState<boolean>(false);
  const [buzzNotification, setBuzzNotification] = useState<BuzzNotification | null>(null);

  // Simuleer real-time updates (in echte app: WebSocket/SSE)
  useEffect(() => {
    const interval = setInterval(() => {
      // Simuleer acceptance na 3 seconden
      setConnectionRequests(prev => 
        prev.map(req => 
          req.status === 'pending' && Date.now() % 10000 < 3000
            ? { ...req, status: 'accepted' as const }
            : req
        )
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  // Luister naar status changes voor popup
  useEffect(() => {
    const acceptedRequest = connectionRequests.find(req => req.status === 'accepted');
    if (acceptedRequest && !showPopup) {
      setShowPopup(true);
      // Auto-hide na 10 seconden
      setTimeout(() => setShowPopup(false), 10000);
    }
  }, [connectionRequests, showPopup]);

  const handleStartChat = () => {
    setShowPopup(false);
    // Hier zou je naar de chat navigeren
    alert('Navigeer naar chat met SuperKonnected 28');
  };

  const handleBuzz = (targetUser: string) => {
    setBuzzNotification({
      from: 'SuperKonnected 28',
      to: targetUser,
      message: 'Je wordt uitgenodigd nu de chat te beginnen',
      timestamp: new Date().toLocaleTimeString()
    });
    
    // Auto-hide buzz na 8 seconden
    setTimeout(() => setBuzzNotification(null), 8000);
  };

  const ConnectionRequestCard = ({ request }: { request: ConnectionRequest }) => (
    <div className="bg-white border rounded-lg p-4 shadow-sm">
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold">
            BS
          </div>
          <div>
            <p className="font-medium">{request.from}</p>
            <p className="text-sm text-gray-500">Verbindingsverzoek</p>
          </div>
        </div>
        <div className="flex items-center space-x-2">
          {request.status === 'pending' && (
            <span className="px-3 py-1 bg-yellow-100 text-yellow-800 rounded-full text-sm">
              Pending
            </span>
          )}
          {request.status === 'accepted' && (
            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm flex items-center">
              <span className="mr-1">✓</span>
              Accepted
            </span>
          )}
        </div>
      </div>
    </div>
  );

  const AcceptancePopup = () => {
    if (!showPopup) return null;
    
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl p-6 max-w-md mx-4 shadow-2xl animate-bounce">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white text-2xl">✓</span>
            </div>
            <h3 className="text-xl font-bold mb-2">Verbinding Geaccepteerd!</h3>
            <p className="text-gray-600 mb-6">
              <strong>SuperKonnected 28</strong> heeft je verbindingsverzoek geaccepteerd
            </p>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowPopup(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Later
              </button>
              <button
                onClick={handleStartChat}
                className="flex-1 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center justify-center"
              >
                <span className="mr-2">💬</span>
                Start Chat
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const BuzzNotification = () => {
    if (!buzzNotification) return null;
    
    return (
      <div className="fixed top-4 right-4 z-50 animate-pulse">
        <div className="bg-white border-2 border-orange-400 rounded-xl p-4 shadow-lg max-w-sm animate-bounce">
          <div className="flex items-start space-x-3">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center text-white font-semibold relative">
              SK
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-orange-400 rounded-full animate-ping"></div>
            </div>
            <div className="flex-1">
              <div className="flex items-center space-x-2 mb-2">
                <span className="text-orange-500">⚡</span>
                <span className="font-semibold text-sm">Buzz ontvangen!</span>
              </div>
              <p className="text-sm text-gray-700 mb-1">{buzzNotification.message}</p>
              <p className="text-xs text-gray-500">van {buzzNotification.from}</p>
            </div>
            <button
              onClick={() => setBuzzNotification(null)}
              className="text-gray-400 hover:text-gray-600"
            >
              <span>✕</span>
            </button>
          </div>
          <div className="mt-3">
            <button
              onClick={() => {
                setBuzzNotification(null);
                handleStartChat();
              }}
              className="w-full px-3 py-2 bg-orange-500 text-white rounded-lg text-sm hover:bg-orange-600 transition-colors flex items-center justify-center"
            >
              <span className="mr-2">💬</span>
              Begin Chat Nu
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-8">Chat Connection System</h1>
        
        {/* User A View - Business Security 28 */}
        <div className="grid lg:grid-cols-2 gap-8">
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">Business Security 28 Dashboard</h2>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm">
                  Persoon A
                </span>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-medium">Verzonden Verzoeken</h3>
                {connectionRequests.map(request => (
                  <ConnectionRequestCard key={request.id} request={request} />
                ))}
              </div>
            </div>
          </div>

          {/* User B View - SuperKonnected 28 */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-sm border">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold">SuperKonnected 28 Dashboard</h2>
                <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                  Persoon B
                </span>
              </div>
              
              <div className="space-y-4">
                <h3 className="font-medium">Online Connecties</h3>
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
                      <span className="font-medium">Business Security 28</span>
                      <span className="text-sm text-green-600">Online</span>
                    </div>
                    <button
                      onClick={() => handleBuzz('Business Security 28')}
                      className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors flex items-center text-sm"
                    >
                      <span className="mr-2">⚡</span>
                      Buzz
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Demo Controls */}
        <div className="mt-8 bg-white rounded-xl p-6 shadow-sm border">
          <h3 className="font-medium mb-4">Demo Controls</h3>
          <div className="flex space-x-4">
            <button
              onClick={() => setShowPopup(true)}
              className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
            >
              Simuleer Acceptatie Popup
            </button>
            <button
              onClick={() => handleBuzz('Business Security 28')}
              className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
            >
              Simuleer Buzz Notificatie
            </button>
          </div>
        </div>
      </div>

      {/* Popups */}
      <AcceptancePopup />
      <BuzzNotification />
    </div>
  );
};

export default ChatConnectionSystem;